import AsyncStorage from '@react-native-async-storage/async-storage';
import { InteractionManager, Platform } from 'react-native';
import type SocketManager from '@utils/socket';
import { userDataIndicatesPaidSubscription } from '@utils/subscriptionBillingGuard';

export type ContextEntryType =
	| 'user_interaction'
	| 'notification'
	| 'ai_request'
	| 'tensorflow_request'
	| 'document_viewing';

export interface ContextEntry {
	id: string;
	timestamp: string;
	type: ContextEntryType;
	content: string;
	tokenCount: number;
	byteCount: number;
	metadata?: Record<string, any>;
}

export interface ContextBreakdown {
	user_interaction: { tokens: number; bytes: number; count: number };
	notification: { tokens: number; bytes: number; count: number };
	ai_request: { tokens: number; bytes: number; count: number };
	tensorflow_request: { tokens: number; bytes: number; count: number };
	document_viewing: { tokens: number; bytes: number; count: number };
}

export interface ContextSettings {
	trackUserInteractions: boolean;
	trackNotifications: boolean;
	trackAIRequests: boolean;
	trackTensorFlowRequests: boolean;
	trackDocumentViewing: boolean;
	userInteractionFilter: 'all' | 'filtered' | 'none';
	/** When true (default), send user_mcp_tools_enabled to /api/ai/help so ML can align OpenAPI/tool discovery with user MCP preferences */
	userMcpToolsEnabled: boolean;
	/** When true, append a rolling context sample (live window + digest + summary heads) to help / proactive AI for richer tool routing (web only) */
	attachInteractionSummaryToAiTools: boolean;
}

/** Compact factual digest from the proactive ingester (optional input to rolling sample). */
export type RollingDigestSnapshot = {
	actionTypeCounts?: Record<string, number>;
	screenTrail?: string[];
	apiPatterns?: string[];
};

export type RollingContextSampleOptions = {
	maxChars?: number;
	/** Recent live context rows to include (most recent first). Default 10. */
	liveEntryCount?: number;
	/** How many summary-history heads to include. Default 2. */
	summaryCount?: number;
	/** Per-summary head char cap. Default 400. */
	summaryHeadChars?: number;
	/** Per live-entry content clip. Default 120. */
	liveClipChars?: number;
	digest?: RollingDigestSnapshot | null;
	weights?: Record<string, number> | null;
	/** Include digest section (default true). Set false when digest is already sent separately. */
	includeDigest?: boolean;
	/** Include canvas weights (default true). */
	includeWeights?: boolean;
};

// Hard window size shown by ContextProgressRing (100%)
const CONTEXT_LIMIT_TOKENS = 200000;
const CONTEXT_LIMIT_BYTES = 200000;

// Request a summary before the window is full so the job (up to ~2 min) lands before overflow
const SUMMARY_TRIGGER_THRESHOLD_TOKENS = CONTEXT_LIMIT_TOKENS * 0.8;
const SUMMARY_TRIGGER_THRESHOLD_BYTES = CONTEXT_LIMIT_BYTES * 0.8;

// When the window still overflows (non-premium, job failed, socket missed), evict oldest live rows down to this
const PRUNE_TARGET_TOKENS = CONTEXT_LIMIT_TOKENS * 0.6;
const PRUNE_TARGET_BYTES = CONTEXT_LIMIT_BYTES * 0.6;

// Minimum time between automatic summary requests
const MIN_SUMMARY_INTERVAL_MS = 5 * 60 * 1000;

// Release the in-flight lock if no completion notification arrives (backend job caps at 2 min)
const SUMMARY_PENDING_TIMEOUT_MS = 3 * 60 * 1000;

// Throttle for limit checks after writes
const LIMIT_CHECK_DELAY_MS = 5000;

// Live (non-summary) entries are clipped so a single Redux payload / AI response cannot flood the window
const MAX_LIVE_ENTRY_CHARS = 2000;

// In 'filtered' mode, identical consecutive interactions inside this window are dropped
const INTERACTION_DEDUPE_MS = 2000;

// Re-check plan status this often so upgrades/downgrades take effect without a reload
const PREMIUM_CACHE_TTL_MS = 30000;

export type ContextSummaryRequestStatus =
	| 'queued'
	| 'completed'
	| 'in_progress'
	| 'empty'
	| 'skipped'
	| 'failed';

type ContextStats = { tokens: number; bytes: number; breakdown: ContextBreakdown };

function emptyBreakdown(): ContextBreakdown {
	return {
		user_interaction: { tokens: 0, bytes: 0, count: 0 },
		notification: { tokens: 0, bytes: 0, count: 0 },
		ai_request: { tokens: 0, bytes: 0, count: 0 },
		tensorflow_request: { tokens: 0, bytes: 0, count: 0 },
		document_viewing: { tokens: 0, bytes: 0, count: 0 },
	};
}

function cloneBreakdown(b: ContextBreakdown): ContextBreakdown {
	return {
		user_interaction: { ...b.user_interaction },
		notification: { ...b.notification },
		ai_request: { ...b.ai_request },
		tensorflow_request: { ...b.tensorflow_request },
		document_viewing: { ...b.document_viewing },
	};
}

// Approximate token counting: ~4 characters per token
function estimateTokens(text: string): number {
	return Math.ceil(text.length / 4);
}

/**
 * Normalize websocket/API payloads: `summary` may be a string, `{ content, metadata }`,
 * or `content` may be non-string. Merges top-level `notification.metadata` with `summary.metadata`.
 */
function extractContextSummaryFromNotification(notification: any): {
	content: string;
	metadata: Record<string, any>;
} | null {
	if (!notification || typeof notification !== 'object') return null;
	const topMeta =
		notification.metadata && typeof notification.metadata === 'object'
			? { ...notification.metadata }
			: {};
	const s = notification.summary;
	let content: string | undefined;
	let meta: Record<string, any> = { ...topMeta };

	if (typeof s === 'string' && s.trim()) {
		content = s.trim();
	} else if (s && typeof s === 'object') {
		const c = s.content;
		if (typeof c === 'string' && c.trim()) content = c.trim();
		else if (c != null && c !== '') content = String(c).trim();
		if (s.metadata && typeof s.metadata === 'object') {
			meta = { ...s.metadata, ...meta };
		}
	}

	if (!content && typeof notification.content === 'string' && notification.content.trim()) {
		content = notification.content.trim();
	}

	if (!content) return null;
	return { content, metadata: meta };
}

function summaryTextByteLength(content: string): number {
	try {
		return new Blob([content]).size;
	} catch {
		return typeof TextEncoder !== 'undefined'
			? new TextEncoder().encode(content).length
			: content.length;
	}
}

/** Bump when adding stores/indexes so initDB reconnects and onupgradeneeded runs. */
const CONTEXT_WINDOW_DB_NAME = 'ContextWindowDB';
const CONTEXT_WINDOW_DB_VERSION = 3;

class ContextTracker {
	private dbInstance: IDBDatabase | null = null;
	private contextQueue: ContextEntry[] = [];
	private isProcessing = false;
	private batchTimeout: NodeJS.Timeout | null = null;
	private readonly batchSize = 10;
	private readonly flushInterval = 2000; // 2 seconds (increased from 1 second)
	/** Single-pass size + breakdown, updated incrementally on writes; TTL picks up other tabs' writes. */
	private stats: ContextStats | null = null;
	private statsTimestamp: number = 0;
	private readonly statsCacheTTL = 30000;
	private summaryWatchdog: ReturnType<typeof setTimeout> | null = null;
	private lastInteractionKey: string | null = null;
	private lastInteractionAt = 0;
	private premiumCache: { value: boolean; at: number } | null = null;
	private settings: ContextSettings = {
		trackUserInteractions: true,
		trackNotifications: true,
		trackAIRequests: true,
		trackTensorFlowRequests: true,
		trackDocumentViewing: true,
		userInteractionFilter: 'filtered',
		userMcpToolsEnabled: true,
		attachInteractionSummaryToAiTools: false,
	};
	private lastSummaryTimestamp: number = 0; // Track when last summary was requested
	private summaryCheckTimeout: NodeJS.Timeout | null = null; // Debounce summary checks
	private socketManager: SocketManager | null = null; // Socket manager for notifications
	private socketListenerSetup: boolean = false; // Track if socket listener is set up
	private pendingSummaryJobId: string | null = null; // Track pending summary job ID
	/** Stable refs so we can off/on without duplicating if DashStack or others rebind the namespace */
	private boundNotificationHandler: ((notification: any) => void) | null = null;
	private boundAgentSuggestionHandler: ((payload: any) => void) | null = null;
	private boundOrchestrationDecisionHandler: ((payload: any) => void) | null = null;
	/** Prevent double-save when DashStack and ContextTracker both receive the same socket event */
	private lastProcessedSummaryKey: string | null = null;
	private lastProcessedSummaryAt = 0;

	constructor() {
		if (Platform.OS === 'web') {
			this.initDB()
				.then(() => this.scheduleSummaryCheck())
				.catch((err) =>
					console.error('Failed to initialize context tracker DB:', err)
				);
		} else {
			InteractionManager.runAfterInteractions(() => {
				this.loadSettings().catch((err) =>
					console.error('Failed to load context settings:', err)
				);
			});
			this.setupSocketListener();
		}
	}

	/** IndexedDB summary_history key: one rolling row per browser, replaced each proactive run. */
	static readonly PROACTIVE_USAGE_DIGEST_ID = 'proactive-usage-digest';

	/**
	 * Cleanup method to clear timeouts and prevent memory leaks
	 * Should be called when context tracker is no longer needed
	 */
	cleanup(): void {
		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
			this.batchTimeout = null;
		}
		if (this.summaryCheckTimeout) {
			clearTimeout(this.summaryCheckTimeout);
			this.summaryCheckTimeout = null;
		}
		if (this.summaryWatchdog) {
			clearTimeout(this.summaryWatchdog);
			this.summaryWatchdog = null;
		}
		// Clean up socket listener (only our handlers — never wipe the whole namespace)
		if (this.socketManager && this.socketListenerSetup) {
			try {
				const socket = (this.socketManager as any).getSocket?.();
				if (socket) {
					if (this.boundNotificationHandler) {
						socket.off('notification', this.boundNotificationHandler);
					}
					if (this.boundAgentSuggestionHandler) {
						socket.off('agent_suggestion', this.boundAgentSuggestionHandler);
					}
					if (this.boundOrchestrationDecisionHandler) {
						socket.off('orchestration_decision', this.boundOrchestrationDecisionHandler);
					}
				}
			} catch (error) {
				console.warn('[ContextTracker] Error cleaning up socket listener:', error);
			}
			this.socketListenerSetup = false;
		}
	}

	async initDB(): Promise<IDBDatabase> {
		if (Platform.OS !== 'web') {
			throw new Error('IndexedDB only available on web platform');
		}

		// Stale handle blocks version upgrades — summary_history never appears and history reads stay empty.
		if (this.dbInstance && this.dbInstance.version < CONTEXT_WINDOW_DB_VERSION) {
			try {
				this.dbInstance.close();
			} catch {
				/* ignore */
			}
			this.dbInstance = null;
		}

		if (this.dbInstance && this.dbInstance.version >= CONTEXT_WINDOW_DB_VERSION) {
			return this.dbInstance;
		}

		return new Promise((resolve, reject) => {
			const request = indexedDB.open(
				CONTEXT_WINDOW_DB_NAME,
				CONTEXT_WINDOW_DB_VERSION
			);

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;
				const tx = (event.target as IDBOpenDBRequest).transaction;

				if (!db.objectStoreNames.contains('context')) {
					const store = db.createObjectStore('context', {
						keyPath: 'id',
						autoIncrement: false,
					});
					store.createIndex('timestamp', 'timestamp', {
						unique: false,
					});
					store.createIndex('type', 'type', { unique: false });
				}

				if (!db.objectStoreNames.contains('settings')) {
					db.createObjectStore('settings', { keyPath: 'key' });
				}

				// Summary history - persists across context clears so modal shows all past summaries
				if (!db.objectStoreNames.contains('summary_history')) {
					const store = db.createObjectStore('summary_history', {
						keyPath: 'id',
						autoIncrement: false,
					});
					store.createIndex('timestamp', 'timestamp', { unique: false });
				} else if (tx) {
					const store = tx.objectStore('summary_history');
					if (!store.indexNames.contains('timestamp')) {
						store.createIndex('timestamp', 'timestamp', { unique: false });
					}
				}
			};

			request.onsuccess = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;
				this.dbInstance = db;
				db.onversionchange = () => {
					try {
						db.close();
					} catch {
						/* ignore */
					}
					this.dbInstance = null;
				};
				this.loadSettings();
				resolve(db);
			};

			request.onerror = (event) => {
				reject((event.target as IDBOpenDBRequest).error);
			};
		});
	}

	private async loadSettings(): Promise<void> {
		if (Platform.OS !== 'web') {
			try {
				const saved = await AsyncStorage.getItem('contextSettings');
				if (saved) {
					this.settings = {
						...this.settings,
						...JSON.parse(saved),
					};
				}
			} catch (error) {
				console.error('Failed to load context settings from AsyncStorage:', error);
			}
			return;
		}

		if (!this.dbInstance) return;

		try {
			const transaction = this.dbInstance.transaction(
				'settings',
				'readonly'
			);
			const store = transaction.objectStore('settings');
			const request = store.get('contextSettings');

			request.onsuccess = () => {
				if (request.result) {
					this.settings = {
						...this.settings,
						...request.result.value,
					};
					if (this.settings.userMcpToolsEnabled === undefined) {
						this.settings.userMcpToolsEnabled = true;
					}
					if (this.settings.attachInteractionSummaryToAiTools === undefined) {
						this.settings.attachInteractionSummaryToAiTools = false;
					}
				}
			};
		} catch (error) {
			console.error('Failed to load context settings:', error);
		}
	}

	async updateSettings(settings: Partial<ContextSettings>): Promise<void> {
		this.settings = { ...this.settings, ...settings };

		if (Platform.OS !== 'web') {
			try {
				await AsyncStorage.setItem(
					'contextSettings',
					JSON.stringify(this.settings)
				);
			} catch (error) {
				console.error('Failed to save context settings to AsyncStorage:', error);
			}
			return;
		}

		if (!this.dbInstance) {
			await this.initDB();
		}

		try {
			const transaction = this.dbInstance!.transaction(
				'settings',
				'readwrite'
			);
			const store = transaction.objectStore('settings');
			await store.put({ key: 'contextSettings', value: this.settings });
		} catch (error) {
			console.error('Failed to save context settings:', error);
		}
	}

	getSettings(): ContextSettings {
		return { ...this.settings };
	}

	async addContextEntry(
		type: ContextEntryType,
		content: string,
		metadata?: Record<string, any>
	): Promise<void> {
		if (Platform.OS !== 'web') return;

		// Summaries must always land in the context window (settings only gate live tracking)
		if (!metadata?.isSummary) {
			if (!this.shouldTrackType(type)) return;
			if (!(await this.hasPremiumAccess())) return;

			if (content.length > MAX_LIVE_ENTRY_CHARS) {
				content = `${content.slice(0, MAX_LIVE_ENTRY_CHARS - 1)}…`;
			}

			if (
				type === 'user_interaction' &&
				this.settings.userInteractionFilter === 'filtered'
			) {
				const now = Date.now();
				if (
					content === this.lastInteractionKey &&
					now - this.lastInteractionAt < INTERACTION_DEDUPE_MS
				) {
					this.lastInteractionAt = now;
					return;
				}
				this.lastInteractionKey = content;
				this.lastInteractionAt = now;
			}
		}

		const tokenCount = estimateTokens(content);
		const byteCount = new Blob([content]).size;

		const entry: ContextEntry = {
			id: `${Date.now()}-${Math.random()}`,
			timestamp: new Date().toISOString(),
			type,
			content,
			tokenCount,
			byteCount,
			metadata,
		};

		this.contextQueue.push(entry);
		this.scheduleBatchProcess();
	}

	private shouldTrackType(type: ContextEntryType): boolean {
		switch (type) {
			case 'user_interaction':
				return (
					this.settings.trackUserInteractions &&
					this.settings.userInteractionFilter !== 'none'
				);
			case 'notification':
				return this.settings.trackNotifications;
			case 'ai_request':
				return this.settings.trackAIRequests;
			case 'tensorflow_request':
				return this.settings.trackTensorFlowRequests;
			case 'document_viewing':
				return this.settings.trackDocumentViewing;
			default:
				return false;
		}
	}

	private scheduleBatchProcess(): void {
		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
		}

		this.batchTimeout = setTimeout(() => {
			this.processBatch();
		}, this.flushInterval);

		if (this.contextQueue.length >= this.batchSize) {
			this.processBatch();
		}
	}

	private async processBatch(): Promise<void> {
		if (this.isProcessing || this.contextQueue.length === 0) return;

		this.isProcessing = true;

		try {
			const db = await this.initDB();
			const transaction = db.transaction('context', 'readwrite');
			const store = transaction.objectStore('context');

			const batch = this.contextQueue.splice(0, this.batchSize);

			const promises = batch.map(
				(entry) =>
					new Promise<void>((resolve, reject) => {
						const request = store.add(entry);
						request.onsuccess = () => resolve();
						request.onerror = () => {
							// Handle duplicate key errors gracefully
							if (request.error?.name === 'ConstraintError') {
								console.warn(
									'Context entry already exists, skipping:',
									entry.id
								);
								resolve(); // Resolve instead of reject to continue processing
							} else {
								reject(request.error);
							}
						};
					})
			);

			await Promise.all(promises);

			this.applyStatsDelta(batch);
			this.scheduleSummaryCheck();

			// If there are more items in queue, schedule next batch
			if (this.contextQueue.length > 0) {
				this.scheduleBatchProcess();
			}
		} catch (error) {
			console.error('Failed to process context batch:', error);
			// Don't re-add items to queue on error to prevent infinite loops
			// Items are already removed from queue, so they'll be lost but won't cause reloads
		} finally {
			this.isProcessing = false;
		}
	}

	async getContextSize(
		useCache: boolean = true
	): Promise<{ tokens: number; bytes: number }> {
		if (Platform.OS !== 'web') return { tokens: 0, bytes: 0 };

		try {
			const stats = await this.loadStats(useCache);
			return { tokens: stats.tokens, bytes: stats.bytes };
		} catch (error) {
			console.error('Failed to get context size:', error);
			return { tokens: 0, bytes: 0 };
		}
	}

	private async loadStats(useCache: boolean): Promise<ContextStats> {
		if (
			useCache &&
			this.stats &&
			Date.now() - this.statsTimestamp < this.statsCacheTTL
		) {
			return this.stats;
		}

		const entries = await this.getContextEntries();
		const stats: ContextStats = { tokens: 0, bytes: 0, breakdown: emptyBreakdown() };
		for (const entry of entries) {
			stats.tokens += entry.tokenCount || 0;
			stats.bytes += entry.byteCount || 0;
			const bucket = stats.breakdown[entry.type];
			if (bucket) {
				bucket.tokens += entry.tokenCount || 0;
				bucket.bytes += entry.byteCount || 0;
				bucket.count += 1;
			}
		}
		this.stats = stats;
		this.statsTimestamp = Date.now();
		return stats;
	}

	private applyStatsDelta(entries: ContextEntry[]): void {
		if (!this.stats) return;
		for (const entry of entries) {
			this.stats.tokens += entry.tokenCount;
			this.stats.bytes += entry.byteCount;
			const bucket = this.stats.breakdown[entry.type];
			if (bucket) {
				bucket.tokens += entry.tokenCount;
				bucket.bytes += entry.byteCount;
				bucket.count += 1;
			}
		}
	}

	private invalidateStats(): void {
		this.stats = null;
		this.statsTimestamp = 0;
	}

	async getContextEntries(limit?: number): Promise<ContextEntry[]> {
		if (Platform.OS !== 'web') return [];

		try {
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction('context', 'readonly');
				const store = transaction.objectStore('context');
				const index = store.index('timestamp');

				// No limit: load all (oldest→newest on index), reverse to most-recent-first.
				if (limit == null || limit <= 0) {
					const request = index.getAll();
					request.onsuccess = () => {
						resolve(request.result.reverse());
					};
					request.onerror = () => reject(request.error);
					return;
				}

				// With limit: walk newest→oldest so we get recent activity, not the oldest N.
				const results: ContextEntry[] = [];
				const request = index.openCursor(null, 'prev');
				request.onsuccess = () => {
					const cursor = request.result;
					if (cursor && results.length < limit) {
						results.push(cursor.value as ContextEntry);
						cursor.continue();
					} else {
						resolve(results);
					}
				};
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to fetch context entries:', error);
			return [];
		}
	}

	async getContextBreakdown(): Promise<ContextBreakdown> {
		if (Platform.OS !== 'web') return emptyBreakdown();

		try {
			const stats = await this.loadStats(true);
			return cloneBreakdown(stats.breakdown);
		} catch (error) {
			console.error('Failed to get context breakdown:', error);
			return emptyBreakdown();
		}
	}

	/** Oldest-first eviction of live rows (summaries are kept) until the window is at or below the target. */
	private async pruneOldestEntries(
		targetTokens: number,
		targetBytes: number
	): Promise<number> {
		const current = await this.loadStats(false);
		let tokens = current.tokens;
		let bytes = current.bytes;
		if (tokens <= targetTokens && bytes <= targetBytes) return 0;

		const db = await this.initDB();
		let removed = 0;
		await new Promise<void>((resolve, reject) => {
			const transaction = db.transaction('context', 'readwrite');
			transaction.oncomplete = () => resolve();
			transaction.onerror = () => reject(transaction.error);
			transaction.onabort = () =>
				reject(transaction.error || new Error('context prune aborted'));
			const request = transaction
				.objectStore('context')
				.index('timestamp')
				.openCursor();
			request.onsuccess = () => {
				const cursor = request.result;
				if (!cursor || (tokens <= targetTokens && bytes <= targetBytes)) return;
				const entry = cursor.value as ContextEntry;
				if (!entry.metadata?.isSummary) {
					cursor.delete();
					tokens -= entry.tokenCount || 0;
					bytes -= entry.byteCount || 0;
					removed += 1;
				}
				cursor.continue();
			};
		});

		this.invalidateStats();
		return removed;
	}

	/**
	 * Save a summary to the history store (persists across context clears)
	 * Called when a summary is generated so the modal can show all past summaries
	 */
	private async saveSummaryToHistory(
		content: string,
		metadata: Record<string, any>,
		options?: { id?: string }
	): Promise<void> {
		if (Platform.OS !== 'web') return;

		try {
			const entry: ContextEntry = {
				id:
					options?.id ??
					`summary-${Date.now()}-${Math.random().toString(36).slice(2)}`,
				timestamp: new Date().toISOString(),
				type: 'ai_request',
				content,
				tokenCount: estimateTokens(content),
				byteCount: summaryTextByteLength(content),
				metadata: {
					...metadata,
					isSummary: true,
				},
			};

			const db = await this.initDB();
			if (!db.objectStoreNames.contains('summary_history')) {
				console.error(
					'[ContextTracker] summary_history store missing after initDB; cannot save summary'
				);
				return;
			}
			await new Promise<void>((resolve, reject) => {
				const transaction = db.transaction('summary_history', 'readwrite');
				transaction.oncomplete = () => resolve();
				transaction.onerror = () => reject(transaction.error);
				transaction.onabort = () =>
					reject(transaction.error || new Error('summary_history transaction aborted'));
				const store = transaction.objectStore('summary_history');
				const request = options?.id ? store.put(entry) : store.add(entry);
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('[ContextTracker] Failed to save summary to history:', error);
		}
	}

	/**
	 * Get all summary entries from history (persisted across context clears)
	 */
	async getSummaryHistoryEntries(): Promise<ContextEntry[]> {
		if (Platform.OS !== 'web') return [];

		try {
			const db = await this.initDB();
			if (!db.objectStoreNames.contains('summary_history')) {
				console.warn(
					'[ContextTracker] summary_history store missing; open modal after app load or clear site data if this persists'
				);
				return [];
			}
			return new Promise((resolve, reject) => {
				const transaction = db.transaction('summary_history', 'readonly');
				transaction.onerror = () => reject(transaction.error);
				const store = transaction.objectStore('summary_history');
				const useIndex = store.indexNames.contains('timestamp');
				const request = useIndex
					? store.index('timestamp').getAll()
					: store.getAll();

				request.onsuccess = () => {
					let results = (request.result as ContextEntry[]) || [];
					if (!useIndex) {
						results = [...results].sort(
							(a, b) =>
								new Date(b.timestamp).getTime() -
								new Date(a.timestamp).getTime()
						);
					} else {
						results = [...results].reverse();
					}
					resolve(results);
				};
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to fetch summary history:', error);
			return [];
		}
	}

	async clearContext(): Promise<void> {
		if (Platform.OS !== 'web') return;

		try {
			// Wait for any ongoing batch processing to complete
			// This prevents race conditions where entries are added after clearing
			while (this.isProcessing) {
				await new Promise(resolve => setTimeout(resolve, 50));
			}
			
			// Clear pending queue first
			this.contextQueue = [];
			
			// Clear any pending batch processing
			if (this.batchTimeout) {
				clearTimeout(this.batchTimeout);
				this.batchTimeout = null;
			}
			
			// Cancel any pending summary checks
			if (this.summaryCheckTimeout) {
				clearTimeout(this.summaryCheckTimeout);
				this.summaryCheckTimeout = null;
			}
			
			this.invalidateStats();
			
			// Clear IndexedDB context store only (summary_history is preserved)
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction('context', 'readwrite');
				const store = transaction.objectStore('context');
				const request = store.clear();

				request.onsuccess = () => {
					this.stats = { tokens: 0, bytes: 0, breakdown: emptyBreakdown() };
					this.statsTimestamp = Date.now();
					resolve();
				};
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to clear context:', error);
		}
	}

	private isSummarizing = false; // Prevent concurrent summary generation

	/**
	 * Process a context-summary / proactive notification payload.
	 * Safe to call from DashStack (primary notification bus) or our own socket listener.
	 */
	public async processIncomingNotification(notification: any): Promise<void> {
		if (!notification || typeof notification !== 'object') return;

		if (
			notification.type === 'context_summary_complete' ||
			notification.type === 'context_summary_failed'
		) {
			await this.handleSummaryNotification(notification);
			return;
		}

		if (notification.type === 'proactive_ai_suggestion') {
			await this.handleProactiveAISuggestion(notification);
		}
	}

	/**
	 * Set up socket listener for context summary notifications
	 * This listens for async summary completion from the backend
	 */
	public async setupSocketListener(): Promise<void> {
		if (Platform.OS !== 'web') {
			return;
		}

		try {
			// Get userId for socket authentication
			let userId: string | null = null;
			try {
				const storeModule = await import('@reduxLocal/persistState');
				const store = storeModule.store;
				if (store) {
					const state = store.getState();
					userId = state?.promiseData?.data?.userData?.id;
				}
			} catch (error) {
				console.warn('[ContextTracker] Could not get userId for socket:', error);
			}

			if (!userId) {
				console.warn('[ContextTracker] No userId available, cannot set up socket listener');
				return;
			}

			// Initialize socket manager for notifications namespace
			if (!this.socketManager) {
				// Lazy require breaks persistState → contextTracker → socket → getId cycle at module init.
				const SocketManagerCtor = require('@utils/socket').default as typeof import('@utils/socket').default;
				this.socketManager = new SocketManagerCtor('notifications', {
					autoconnect: false
				});
			}

			const socket = this.socketManager.getSocket(userId);
			if (!socket) {
				console.warn('[ContextTracker] Socket not available for notifications');
				return;
			}

			// Connect socket if not already connected (await to ensure ready before job completes)
			if (!socket.connected) {
				await this.socketManager.connect();
			}

			if (!this.boundNotificationHandler) {
				this.boundNotificationHandler = async (notification: any) => {
					if (
						notification?.type === 'context_summary_complete' ||
						notification?.type === 'context_summary_failed' ||
						notification?.type === 'proactive_ai_suggestion'
					) {
						await this.processIncomingNotification(notification);
						return;
					}
					if (notification?.type === 'agent_suggestion') {
						try {
							const { processAgentSuggestionPayload } = await import('./agentSuggestionEvents');
							await processAgentSuggestionPayload(notification);
						} catch (err) {
							console.warn('[ContextTracker] agent_suggestion handler failed:', err);
						}
						return;
					}
					if (notification?.type === 'orchestration_decision') {
						try {
							const { processOrchestrationDecisionPayload } = await import('./orchestrationDecisionEvents');
							processOrchestrationDecisionPayload(notification);
						} catch (err) {
							console.warn('[ContextTracker] orchestration_decision handler failed:', err);
						}
					}
				};
			}

			if (!this.boundAgentSuggestionHandler) {
				this.boundAgentSuggestionHandler = async (payload: any) => {
					try {
						const { processAgentSuggestionPayload } = await import('./agentSuggestionEvents');
						await processAgentSuggestionPayload({ ...payload, type: 'agent_suggestion' });
					} catch (err) {
						console.warn('[ContextTracker] agent_suggestion event failed:', err);
					}
				};
			}

			if (!this.boundOrchestrationDecisionHandler) {
				this.boundOrchestrationDecisionHandler = async (payload: any) => {
					try {
						const { processOrchestrationDecisionPayload } = await import('./orchestrationDecisionEvents');
						processOrchestrationDecisionPayload(payload);
					} catch (err) {
						console.warn('[ContextTracker] orchestration_decision failed:', err);
					}
				};
			}

			// Always re-bind: DashStack previously used removeAllListeners('notification'),
			// which silently dropped our handler while leaving socketListenerSetup=true.
			socket.off('notification', this.boundNotificationHandler);
			socket.on('notification', this.boundNotificationHandler);
			socket.off('agent_suggestion', this.boundAgentSuggestionHandler);
			socket.on('agent_suggestion', this.boundAgentSuggestionHandler);
			socket.off('orchestration_decision', this.boundOrchestrationDecisionHandler);
			socket.on('orchestration_decision', this.boundOrchestrationDecisionHandler);

			this.socketListenerSetup = true;
			console.log('[ContextTracker] Socket listener set up for context summary, proactive AI, and agent suggestion notifications');
		} catch (error) {
			console.error('[ContextTracker] Failed to set up socket listener:', error);
		}
	}

	private shouldSkipDuplicateSummary(key: string): boolean {
		const now = Date.now();
		if (
			this.lastProcessedSummaryKey === key &&
			now - this.lastProcessedSummaryAt < 15000
		) {
			console.log('[ContextTracker] Skipping duplicate summary notification');
			return true;
		}
		this.lastProcessedSummaryKey = key;
		this.lastProcessedSummaryAt = now;
		return false;
	}

	/**
	 * Handle summary notification from backend
	 */
	private async handleSummaryNotification(notification: any): Promise<void> {
		if (notification.type === 'context_summary_complete') {
			console.log('[ContextTracker] Received context summary completion notification', notification);

			const extracted = extractContextSummaryFromNotification(notification);
			if (!extracted) {
				console.warn(
					'[ContextTracker] Summary notification missing extractable content — ignoring (waiting for payload with summary.content or summary string)'
				);
				return;
			}

			const { content, metadata: rawMeta } = extracted;
			const dedupeKey = `complete:${rawMeta.jobId || rawMeta.requestId || ''}:${content.slice(0, 80)}`;
			if (this.shouldSkipDuplicateSummary(dedupeKey)) {
				return;
			}

			try {
				if (
					rawMeta.originalEntryCount === undefined ||
					rawMeta.originalTokenCount === undefined
				) {
					console.warn(
						'[ContextTracker] Summary notification missing some metadata fields, proceeding anyway'
					);
				}

				const summaryMetadata = {
					...rawMeta,
					isSummary: true,
					originalEntryCount: rawMeta.originalEntryCount ?? 0,
					summarizedAt: new Date().toISOString(),
				};

				// Persist history BEFORE clearing context so a failure in clear/add does not drop the summary from history
				await this.saveSummaryToHistory(content, summaryMetadata);
				await this.clearContext();
				await this.addContextEntry('ai_request', content, summaryMetadata);

				// Step 4: Wait for batch processing to complete and ensure the summary entry is saved
				// This prevents the summary from being lost and ensures context is properly reset
				while (this.isProcessing || this.contextQueue.length > 0) {
					await new Promise(resolve => setTimeout(resolve, 50));
				}
				
				// Wait an additional tick to ensure IndexedDB transaction completes
				await new Promise(resolve => setTimeout(resolve, 0));

				this.invalidateStats();

				// Dispatch custom event to notify UI components (like ContextProgressRing) to refresh
				if (Platform.OS === 'web' && typeof window !== 'undefined') {
					window.dispatchEvent(
						new CustomEvent('contextSummaryGenerated', {
							detail: {
								originalTokenCount: rawMeta.originalTokenCount,
								summaryTokenCount: rawMeta.summaryTokenCount,
								reductionPercentage: rawMeta.reductionPercentage,
							},
						})
					);
				}

				console.log(
					`[ContextTracker] Context summary processed successfully. Reduced from ${rawMeta.originalTokenCount || 'unknown'} to ${rawMeta.summaryTokenCount || 'unknown'} tokens (${rawMeta.reductionPercentage ? rawMeta.reductionPercentage.toFixed(1) : 'unknown'}% reduction)`
				);
				
				// Update timestamp to prevent immediate re-triggering of summary
				this.lastSummaryTimestamp = Date.now();
			} catch (error) {
				console.error('[ContextTracker] Error processing summary notification:', error);
			} finally {
				this.pendingSummaryJobId = null;
				this.setSummarizing(false);
			}
		} else if (notification.type === 'context_summary_failed') {
			console.error('[ContextTracker] Context summary failed:', notification.error);
			this.pendingSummaryJobId = null;
			this.setSummarizing(false);

			// Handle specific error types
			const errorMessage = notification.error || 'Unknown error';
			if (errorMessage.includes('Premium') || errorMessage.includes('premium')) {
				console.warn(
					'[ContextTracker] Context summary denied: Premium subscription required'
				);
				// Don't retry on premium errors - user needs to upgrade
				this.lastSummaryTimestamp = Date.now();
			} else if (
				errorMessage.includes('unavailable') ||
				errorMessage.includes('timeout') ||
				errorMessage.includes('ECONNREFUSED') ||
				errorMessage.includes('ETIMEDOUT')
			) {
				console.warn(
					'[ContextTracker] Summarization service unavailable, will retry after interval'
				);
				// Allow retry after half interval for service errors
				this.lastSummaryTimestamp =
					Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			} else {
				// For other errors, allow retry after half interval
				console.warn(
					'[ContextTracker] Unexpected error during summarization, will retry after interval'
				);
				this.lastSummaryTimestamp =
					Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			}
		}
	}

	/**
	 * Handle proactive AI suggestion — same lifecycle as full context summary:
	 * clear tracked interactions (resets the ring), persist one rolling digest in history,
	 * re-inject the suggestion as the current ai_request context.
	 */
	private async handleProactiveAISuggestion(notification: any): Promise<void> {
		const raw =
			notification.message ||
			notification.response ||
			(typeof notification.summary === 'string' ? notification.summary : notification.summary?.content) ||
			'';
		const content = typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
		if (!content) {
			console.warn('[ContextTracker] Proactive AI suggestion missing content, skipping');
			return;
		}

		const dedupeKey = `proactive:${notification.requestId || notification.flowId || ''}:${content.slice(0, 80)}`;
		if (this.shouldSkipDuplicateSummary(dedupeKey)) {
			return;
		}

		try {
			const metadata = {
				isSummary: true,
				isProactiveAI: true,
				rollingProactiveDigest: true,
				originalEntryCount: notification.actionCount || 0,
				originalTokenCount: 0,
				summaryTokenCount: estimateTokens(content),
				reductionPercentage: 0,
				actionType: notification.actionType || 'unknown',
				requestId: notification.requestId,
				flowId: notification.flowId,
				summarizedAt: new Date().toISOString(),
			};

			await this.saveSummaryToHistory(content, metadata, {
				id: ContextTracker.PROACTIVE_USAGE_DIGEST_ID,
			});
			await this.clearContext();
			await this.addContextEntry('ai_request', content, metadata);

			while (this.isProcessing || this.contextQueue.length > 0) {
				await new Promise((resolve) => setTimeout(resolve, 50));
			}
			await new Promise((resolve) => setTimeout(resolve, 0));

			this.invalidateStats();

			if (Platform.OS === 'web' && typeof window !== 'undefined') {
				window.dispatchEvent(
					new CustomEvent('contextSummaryGenerated', { detail: metadata })
				);
			}

			console.log(
				`[ContextTracker] Proactive AI digest applied; context cleared (actionType: ${metadata.actionType})`
			);
		} catch (error) {
			console.error('[ContextTracker] Failed to apply proactive AI suggestion:', error);
		}
	}

	/**
	 * Check if user is logged in
	 * Returns true if user data exists in the store
	 */
	private async isUserLoggedIn(): Promise<boolean> {
		try {
			// Dynamically import store to avoid circular dependencies
			// Only import when needed (web platform only)
			if (Platform.OS !== 'web') {
				return false;
			}

			// Use dynamic import to avoid circular dependency issues
			const storeModule = await import('@reduxLocal/persistState');
			const store = storeModule.store;

			if (!store) {
				return false;
			}

			const state = store.getState();
			const user = state?.promiseData?.data?.userData;

			// User is logged in if user data exists and has an ID
			return !!(user && user.id);
		} catch (error) {
			console.warn('Failed to check user login status:', error);
			return false; // Default to not logged in on error
		}
	}

	/**
	 * Context tracking, the ring, and AI summaries are premium-only.
	 * Same rule as useCanAccessPaidFeatures (paid subscription, not in learning mode).
	 */
	private async isPremiumUser(): Promise<boolean> {
		if (Platform.OS !== 'web') return false;
		try {
			const { store } = await import('@reduxLocal/persistState');
			const state = store?.getState?.();
			const user = state?.promiseData?.data?.userData;
			if (!user?.id) return false;
			if (state?.app?.learningMode === true || user.accountType === 'learning' || user.isLearningAccount === true) {
				return false;
			}
			return (
				user.isPremium === true ||
				user.subscriptionStatus === 'active' ||
				userDataIndicatesPaidSubscription(user)
			);
		} catch (error) {
			console.warn('Failed to check premium status:', error);
			return false;
		}
	}

	/** Cached premium check for the hot write path (every Redux action goes through addContextEntry). */
	async hasPremiumAccess(): Promise<boolean> {
		const now = Date.now();
		if (this.premiumCache && now - this.premiumCache.at < PREMIUM_CACHE_TTL_MS) {
			return this.premiumCache.value;
		}
		const value = await this.isPremiumUser();
		// Before userData hydrates / login, don't pin a negative result for the whole TTL
		if (value || (await this.isUserLoggedIn())) {
			this.premiumCache = { value, at: now };
		}
		return value;
	}

	/**
	 * Throttled (not debounced) so continuous activity cannot postpone the check forever.
	 * Runs after every batch write and once at startup.
	 */
	private scheduleSummaryCheck(): void {
		if (Platform.OS !== 'web' || this.summaryCheckTimeout) return;

		this.summaryCheckTimeout = setTimeout(() => {
			this.summaryCheckTimeout = null;
			this.enforceContextLimits().catch((err) => {
				console.error('[ContextTracker] Context limit check failed:', err);
			});
		}, LIMIT_CHECK_DELAY_MS);
	}

	/**
	 * At 80% request a summary (premium; resets the window when the job completes).
	 * At 100% evict the oldest live rows so the window never grows unbounded when a summary
	 * is unavailable, still in flight, or its notification was missed.
	 */
	private async enforceContextLimits(): Promise<void> {
		const size = await this.getContextSize(true);

		// Downgraded / free accounts: drop any window left over from a premium period
		if (!(await this.hasPremiumAccess())) {
			if ((size.tokens > 0 || size.bytes > 0) && (await this.isUserLoggedIn())) {
				await this.clearContext();
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('contextWindowUpdated', { detail: { cleared: true } }));
				}
			}
			return;
		}

		if (
			size.tokens >= SUMMARY_TRIGGER_THRESHOLD_TOKENS ||
			size.bytes >= SUMMARY_TRIGGER_THRESHOLD_BYTES
		) {
			await this.requestSummary();
		}

		if (size.tokens >= CONTEXT_LIMIT_TOKENS || size.bytes >= CONTEXT_LIMIT_BYTES) {
			const removed = await this.pruneOldestEntries(PRUNE_TARGET_TOKENS, PRUNE_TARGET_BYTES);
			if (removed > 0 && typeof window !== 'undefined') {
				window.dispatchEvent(new CustomEvent('contextWindowUpdated', { detail: { pruned: removed } }));
			}
		}
	}

	isSummaryInFlight(): boolean {
		return this.isSummarizing;
	}

	private setSummarizing(value: boolean): void {
		this.isSummarizing = value;
		if (!value && this.summaryWatchdog) {
			clearTimeout(this.summaryWatchdog);
			this.summaryWatchdog = null;
		}
		if (Platform.OS === 'web' && typeof window !== 'undefined') {
			window.dispatchEvent(
				new CustomEvent('contextSummaryStateChanged', { detail: { inFlight: value } })
			);
		}
	}

	private startSummaryWatchdog(): void {
		if (this.summaryWatchdog) clearTimeout(this.summaryWatchdog);
		this.summaryWatchdog = setTimeout(() => {
			this.summaryWatchdog = null;
			if (!this.isSummarizing) return;
			console.warn('[ContextTracker] No summary notification received in time; releasing lock');
			this.pendingSummaryJobId = null;
			this.lastSummaryTimestamp = Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			this.setSummarizing(false);
			this.scheduleSummaryCheck();
		}, SUMMARY_PENDING_TIMEOUT_MS);
	}

	/**
	 * Single entry point for summary requests (auto limit check, ContextProgressRing, settings card).
	 * Automatic requests respect login / premium / min-interval; manual requests only skip when one is
	 * already in flight and rethrow API errors so the UI can surface them.
	 */
	async requestSummary(
		options: { manual?: boolean } = {}
	): Promise<ContextSummaryRequestStatus> {
		if (Platform.OS !== 'web') return 'skipped';
		const manual = options.manual === true;

		if (this.isSummarizing) return 'in_progress';

		if (!(await this.hasPremiumAccess())) {
			if (manual) {
				throw Object.assign(new Error('Premium subscription required'), { requiresPremium: true });
			}
			return 'skipped';
		}

		if (!manual && Date.now() - this.lastSummaryTimestamp < MIN_SUMMARY_INTERVAL_MS) {
			return 'skipped';
		}

		// Oldest → newest: the API/backend trimmers keep the tail of the array as "most recent"
		const entries = (await this.getContextEntries()).reverse();
		if (entries.length === 0) return 'empty';

		this.setSummarizing(true);
		this.lastSummaryTimestamp = Date.now();

		try {
			await this.setupSocketListener();
			const { summarizeContext } = await import('@api/api');
			const response = await summarizeContext(entries);

			if (!response?.success) {
				throw new Error(response?.error || 'Invalid response from summarization service');
			}

			if (response.summary?.content) {
				await this.processSummaryResponse(response.summary, entries.length);
				this.setSummarizing(false);
				return 'completed';
			}

			// Backend replies { status: 'queued' } (no jobId) or { status: 'already_queued', jobId }
			this.pendingSummaryJobId = response.jobId ?? null;
			this.startSummaryWatchdog();
			return 'queued';
		} catch (error: any) {
			console.error('[ContextTracker] Failed to queue context summary:', error);

			if (
				error?.requiresPremium ||
				error?.response?.status === 403 ||
				error?.response?.status === 400
			) {
				this.lastSummaryTimestamp = Date.now();
			} else {
				this.lastSummaryTimestamp = Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			}

			this.pendingSummaryJobId = null;
			this.setSummarizing(false);
			if (manual) throw error;
			return 'failed';
		}
	}

	/**
	 * Process summary response (used for backward compatibility or direct processing)
	 * When context window is full: clears old context and saves summary for reference
	 */
	public async processSummaryResponse(
		summary: any,
		originalEntryCount: number
	): Promise<void> {
		if (!summary || typeof summary !== 'object') {
			console.warn('[ContextTracker] processSummaryResponse: invalid summary object');
			return;
		}
		let content: string;
		if (typeof summary.content === 'string') content = summary.content.trim();
		else if (summary.content != null && summary.content !== '')
			content = String(summary.content).trim();
		else {
			console.warn('[ContextTracker] processSummaryResponse: missing content');
			return;
		}
		if (!content) return;

		const metadata = summary.metadata || {};
		if (
			metadata.originalEntryCount === undefined ||
			metadata.originalTokenCount === undefined
		) {
			console.warn(
				'[ContextTracker] Summary response missing some metadata fields, proceeding anyway'
			);
		}

		const summaryMetadata = {
			...metadata,
			isSummary: true,
			originalEntryCount,
			summarizedAt: new Date().toISOString(),
		};

		await this.saveSummaryToHistory(content, summaryMetadata);
		await this.clearContext();
		await this.addContextEntry('ai_request', content, summaryMetadata);

		// Step 3: Wait for batch processing to complete and ensure the summary entry is saved
		// This prevents the summary from being lost and ensures context is properly reset
		while (this.isProcessing || this.contextQueue.length > 0) {
			await new Promise(resolve => setTimeout(resolve, 50));
		}
		
		// Wait an additional tick to ensure IndexedDB transaction completes
		await new Promise(resolve => setTimeout(resolve, 0));

		this.invalidateStats();

		// Dispatch custom event to notify UI components (like ContextProgressRing) to refresh
		if (Platform.OS === 'web' && typeof window !== 'undefined') {
			window.dispatchEvent(
				new CustomEvent('contextSummaryGenerated', {
					detail: {
						originalTokenCount: metadata.originalTokenCount,
						summaryTokenCount: metadata.summaryTokenCount,
						reductionPercentage: metadata.reductionPercentage,
					},
				})
			);
		}

		console.log(
			`[ContextTracker] Context summary processed successfully. Reduced from ${metadata.originalTokenCount || 'unknown'} to ${metadata.summaryTokenCount || 'unknown'} tokens (${metadata.reductionPercentage ? metadata.reductionPercentage.toFixed(1) : 'unknown'}% reduction)`
		);
		
		// Update timestamp to prevent immediate re-triggering of summary
		this.lastSummaryTimestamp = Date.now();
	}
}

// Create singleton instance
const contextTracker = new ContextTracker();

// Export functions
export const addContextEntry = (
	type: ContextEntryType,
	content: string,
	metadata?: Record<string, any>
) => contextTracker.addContextEntry(type, content, metadata);

export const getContextSize = (useCache: boolean = true) =>
	contextTracker.getContextSize(useCache);

export const getContextEntries = (limit?: number) =>
	contextTracker.getContextEntries(limit);

export const getContextBreakdown = () => contextTracker.getContextBreakdown();

export const clearContext = () => contextTracker.clearContext();

export const requestContextSummary = (options?: { manual?: boolean }) =>
	contextTracker.requestSummary(options);

export const isContextSummaryInFlight = () => contextTracker.isSummaryInFlight();

export const updateContextSettings = (settings: Partial<ContextSettings>) =>
	contextTracker.updateSettings(settings);

export const getContextSettings = () => contextTracker.getSettings();

/**
 * Process a summary response - clears old context and saves summary for reference
 * This is used when a summary is generated and needs to replace the old context
 */
export const processSummaryResponse = async (
	summary: any,
	originalEntryCount: number
): Promise<void> => {
	return contextTracker.processSummaryResponse(summary, originalEntryCount);
};

/**
 * Set up socket listener for context summary notifications
 * This ensures the socket listener is ready to receive summary completion notifications
 */
export const setupSocketListener = async (): Promise<void> => {
	return contextTracker.setupSocketListener();
};

/**
 * Persist a context_summary_* / proactive_ai_suggestion notification into ContextWindowDB.
 * Prefer calling this from the app's primary notification bus (DashStack) so saves are not
 * dependent on a separate ContextTracker socket listener surviving removeAllListeners.
 */
export const processContextSummaryNotification = async (
	notification: any
): Promise<void> => {
	return contextTracker.processIncomingNotification(notification);
};

/**
 * Get all summary entries from history (persisted across context clears)
 * Returns entries from summary_history store for the modal display
 */
export const getSummaryEntries = async (): Promise<ContextEntry[]> => {
	if (Platform.OS !== 'web') return [];

	try {
		return await contextTracker.getSummaryHistoryEntries();
	} catch (error) {
		console.error('Failed to get summary entries:', error);
		return [];
	}
};

function clipOneLine(text: string, max: number): string {
	const t = String(text || '')
		.replace(/\s+/g, ' ')
		.trim();
	if (!t) return '';
	return t.length <= max ? t : `${t.slice(0, Math.max(0, max - 1))}…`;
}

function formatTopActionCounts(
	counts: Record<string, number> | undefined,
	limit = 8
): string {
	if (!counts || !Object.keys(counts).length) return '';
	return Object.entries(counts)
		.sort((a, b) => (b[1] || 0) - (a[1] || 0))
		.slice(0, limit)
		.map(([k, v]) => `${k}:${v}`)
		.join(', ');
}

/**
 * Fixed-budget rolling context sample for Help + proactive AI when
 * "attach interaction summary" is enabled (Settings → Context).
 * Combines live window rows, factual digest, summary heads, and optional weights.
 */
export const getRollingContextSample = async (
	options: RollingContextSampleOptions = {}
): Promise<string | undefined> => {
	if (Platform.OS !== 'web') return undefined;
	const settings = contextTracker.getSettings();
	if (!settings.attachInteractionSummaryToAiTools) return undefined;
	if (!(await contextTracker.hasPremiumAccess())) return undefined;

	const maxChars = Math.min(Math.max(options.maxChars ?? 2400, 400), 8000);
	const liveEntryCount = options.liveEntryCount ?? 10;
	const summaryCount = options.summaryCount ?? 2;
	const summaryHeadChars = options.summaryHeadChars ?? 400;
	const liveClipChars = options.liveClipChars ?? 120;
	const includeDigest = options.includeDigest !== false;
	const includeWeights = options.includeWeights !== false;

	try {
		let digest: RollingDigestSnapshot | null | undefined = options.digest;
		if (includeDigest && digest == null) {
			try {
				const mod = await import('@services/proactiveActionIngester');
				digest = mod.getFactualDigestSnapshot?.() ?? null;
			} catch {
				digest = null;
			}
		}

		let weights: Record<string, number> | null | undefined = options.weights;
		if (includeWeights && weights == null) {
			/* caller may pass; Help leaves unset — weights optional */
			weights = null;
		}

		const [liveEntries, summaries] = await Promise.all([
			contextTracker.getContextEntries(liveEntryCount),
			contextTracker.getSummaryHistoryEntries(),
		]);

		const sections: string[] = ['=== Rolling context sample ==='];

		if (includeDigest && digest) {
			const digParts: string[] = [];
			if (digest.screenTrail?.length) {
				digParts.push(`Screens: ${digest.screenTrail.slice(0, 12).join(' → ')}`);
			}
			const topActions = formatTopActionCounts(digest.actionTypeCounts);
			if (topActions) digParts.push(`Top actions: ${topActions}`);
			if (digest.apiPatterns?.length) {
				digParts.push(`API: ${digest.apiPatterns.slice(0, 12).join('; ')}`);
			}
			if (digParts.length) {
				sections.push('[Digest]', ...digParts);
			}
		}

		if (liveEntries?.length) {
			const lines = liveEntries.slice(0, liveEntryCount).map((e) => {
				const clip = clipOneLine(e.content, liveClipChars);
				return clip ? `- [${e.type}] ${clip}` : null;
			}).filter(Boolean) as string[];
			if (lines.length) {
				sections.push('[Recent activity]', ...lines);
			}
		}

		if (summaries?.length) {
			const heads = summaries.slice(0, summaryCount).map((s, i) => {
				const head = clipOneLine(s.content, summaryHeadChars);
				return head ? `--- summary ${i + 1} ---\n${head}` : null;
			}).filter(Boolean) as string[];
			if (heads.length) {
				sections.push('[Summaries]', ...heads);
			}
		}

		if (includeWeights && weights && Object.keys(weights).length) {
			const w = Object.entries(weights)
				.sort((a, b) => (b[1] || 0) - (a[1] || 0))
				.slice(0, 12)
				.map(([k, v]) => `${k}:${v}`)
				.join(', ');
			if (w) sections.push(`[Focus weights] ${w}`);
		}

		// Need at least digest, activity, or summaries to be useful
		const hasBody = sections.length > 1;
		if (!hasBody) return undefined;

		let out = sections.join('\n');
		if (out.length > maxChars) {
			out = `${out.slice(0, Math.max(0, maxChars - 1))}…`;
		}
		return out.trim() || undefined;
	} catch {
		return undefined;
	}
};

/**
 * @deprecated Prefer getRollingContextSample. Kept for callers that still pass maxChars only.
 * Latest rolling sample for proactive AI when "attach interaction summary" is enabled.
 */
export const getProactiveInteractionSummaryExcerpt = async (
	maxChars = 2000
): Promise<string | undefined> => {
	return getRollingContextSample({
		maxChars,
		includeDigest: false,
		includeWeights: false,
	});
};

export default contextTracker;
